import JsonView from '@uiw/react-json-view'
import {monokaiTheme} from '@uiw/react-json-view/monokai'
import classNames from 'classnames'
import {useCallback, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import conditionTypes from '../../../data/conditionTypes.json'
import {CheckIcon, DownIcon, MaximizeIcon, UpIcon} from '../../../icons'
import {colors} from '../../../utils/colors'
import DateHelper from '../../../utils/date'
import {formatHtml} from '../../../utils/formatHtml'
import {locationManager} from '../../../utils/locations'
import TextHelper from '../../../utils/text'
import Badge from '../../core/badge'
import CustomCodeViewer from '../../core/codeViewer'
import CopyButton from '../../core/copyButton'
import Modal from '../../core/modal'
import Table from '../../core/table'
import Tooltip from '../../core/tooltip'
import text from './text.json'
import {isValidJson} from '../../../utils/object'

const htmlTabs = {
    code: {key: 'html_code', label: 'HTML code'},
    preview: {key: 'html_preview', label: 'Preview'}
}

const CONTENT_LENGTH_KEY_NAME = 'Content-Length'

const allTypes = conditionTypes.content_types

const HtmlViewer = ({logData, isDarkMode, activeTab, setActiveTab}) => {
    const tabItems = useMemo(() => Object.values(htmlTabs), [])

    return (
        <>
            <div className={classNames('menu', {dark: isDarkMode})}>
                {tabItems.map(({key, label}) => (
                    <span
                        key={key}
                        className={classNames('menu-tab', {active: activeTab === key})}
                        onClick={() => setActiveTab(key)}
                    >
                        {label}
                    </span>
                ))}
            </div>
            {activeTab === htmlTabs.code.key ? (
                <CustomCodeViewer
                    language={allTypes.text_html.label}
                    code={formatHtml(logData?.response_record?.body)}
                    wrapLongLines
                />
            ) : (
                <div
                    className={classNames('html-preview', {dark: isDarkMode})}
                    dangerouslySetInnerHTML={{__html: logData?.response_record?.body}}
                />
            )}
        </>
    )
}

const ResponseLogsTable = ({data}) => {
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [activeTab, setActiveTab] = useState(htmlTabs.code.key)
    const [selectedLog, setSelectedLog] = useState(null)

    const tableHeader = useMemo(() => [
        {label: 'Checked At (local time)', className: 'min-width'},
        {label: 'Location', className: 'min-width-150'},
        {label: '⁠Response time', className: 'min-width-150'},
        {label: 'Status code', className: 'min-width-120'},
        {label: 'Headers', className: 'min-width-95'},
        {label: 'Response', className: 'min-width-250'}
    ], [])

    const openLogModal = useCallback((log, viewHeaders = false) => {
        setSelectedLog({...log, view_headers: viewHeaders})
        setActiveTab(htmlTabs.code.key)
    }, [])

    const getContentType = useCallback(contentType => {
        return Object.values(allTypes).find(content => content.value === contentType)
    }, [])

    const renderLocation = (log) => {
        const loc = locationManager.allLocations[log?.location]
        return (
            <div className='response-region'>
                <Tooltip content={loc?.location}>
                    <span className='location-icon'>{loc?.icon}</span>
                </Tooltip>
                <Tooltip content={log?.is_up ? text.up : text.down}>
                    {log?.is_up ? <UpIcon /> : <DownIcon />}
                </Tooltip>
                <Tooltip content={log?.approved ? text.approved : text.not_approved}>
                    <CheckIcon
                        fill={log?.approved ? colors.colorGreen : colors.colorGray}
                        width={20}
                        height={18}
                    />
                </Tooltip>
            </div>
        )
    }

    const renderDate = time => {
        const date = new Date(Date.parse(time))

        return (
            <span className="date">
                <span
                    className="checked-at">{[DateHelper.addZero(date.getHours()), DateHelper.addZero(date.getMinutes())].join(':')}</span>
                <span>{[date.getFullYear(), DateHelper.addZero(date.getMonth() + 1), DateHelper.addZero(date.getDate())].join('.')}</span>
            </span>
        )
    }

    const renderResponseCell = useCallback((log) => {
        const contentType = getContentType(log?.response_record?.content_type)
        const isKnownContentType = !!contentType?.value
        const responseTypeLabel = contentType?.label?.toUpperCase()
        const tooltipText = `${text.expand}${responseTypeLabel || ''}`
        const rawBody = log?.response_record?.body
        const parsedBody = isValidJson(rawBody) ? JSON.parse(rawBody) : rawBody

        const responseBody = !parsedBody
            ? Number(log?.response_record?.headers?.[CONTENT_LENGTH_KEY_NAME]?.[0]) > 0
                ? text.too_big_response
                : text.empty
            : isKnownContentType
                ? responseTypeLabel
                : (
                    <Tooltip
                        trigger='mouseenter'
                        content={<span dangerouslySetInnerHTML={{__html: parsedBody}} />}
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: TextHelper.textLengthChecker(parsedBody, 15)
                            }}
                            className={classNames('response-body', {dark: isDarkMode})}
                        />
                    </Tooltip>
                )

        return (
            <>
                <Badge color={responseBody === text.too_big_response ?
                    'danger'
                    : !parsedBody ?
                        'muted'
                        : contentType?.color || 'purple'}>
                    {responseBody}
                </Badge>
                {isKnownContentType && !!parsedBody && (
                    <Tooltip trigger='mouseenter' content={tooltipText}>
                        <button
                            onClick={() => openLogModal(log)}
                            className={classNames('table-action-btn', {dark: isDarkMode})}
                        >
                            <MaximizeIcon stroke={isDarkMode ? colors.colorBgFilter : colors.colorPrimary} />
                        </button>
                    </Tooltip>
                )}
            </>
        )
    }, [getContentType, isDarkMode, openLogModal])

    const tableBody = data?.map((log) => [
        <span className='min-width'>{renderDate(log?.checked_at)}</span>,
        <span className='min-width-150'>{renderLocation(log)}</span>,
        <span className='min-width-150'>{log?.response_record?.response_time_ms} ms</span>,
        <span className='min-width-120'>{log?.response_record?.status_code}</span>,
        <span className='min-width-95'>
            {
                log?.response_record?.headers ?
                    <button
                        className={classNames('reset-button', {dark: isDarkMode})}
                        onClick={() => openLogModal(log, true)}
                    >
                        {text.view}
                    </button> :
                    <Badge color='muted'>{text.empty}</Badge>
            }
        </span>,
        <span className='min-width-250 gap-10'>{renderResponseCell(log)}</span>
    ])

    const selectedContentTypeLabel = selectedLog && getContentType(selectedLog?.response_record?.content_type)?.label?.toUpperCase()

    return (
        <>
            {selectedLog && (
                <Modal
                    title={
                        selectedLog?.response_record?.content_type === allTypes.text_html.value && activeTab === htmlTabs.preview.key
                            ? text.html_preview_title
                            : selectedContentTypeLabel
                                ? TextHelper.replaceAllChars(
                                    text.you_can_copy,
                                    ':response_type',
                                    selectedLog?.view_headers ? text.json :
                                        selectedLog?.response_record?.content_type === allTypes.other.value
                                            ? text.response_body
                                            : selectedContentTypeLabel
                                )
                                : ''
                    }
                    isOpen={!!selectedLog}
                    closeModal={() => setSelectedLog(null)}
                    id='large-modal'
                    contentClassName='overflow-auto response-log-popup'
                >
                    {selectedLog?.response_record?.content_type === allTypes.application_json.value || selectedLog?.view_headers ? (
                        <div className={classNames('json-modal-body', {dark: isDarkMode})}>
                            <JsonView
                                value={selectedLog?.view_headers ? selectedLog?.response_record?.headers : JSON.parse(selectedLog?.response_record?.body)}
                                style={monokaiTheme}
                                displayObjectSize={false}
                                className='response-log-json-view'
                                displayDataTypes={false}
                            />
                            <CopyButton text={JSON.stringify(
                                selectedLog?.view_headers
                                    ? selectedLog?.response_record?.headers
                                    : selectedLog?.response_record?.body,
                                null,
                                2
                            )} />
                        </div>
                    ) : selectedLog?.response_record?.content_type === allTypes.text_html.value ? (
                        <HtmlViewer
                            logData={selectedLog}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            isDarkMode={isDarkMode}
                        />
                    ) : (
                        <span className={classNames('other-type-response', {dark: isDarkMode})}>
                            {selectedLog?.response_record?.body}
                            {getContentType(selectedLog?.response_record?.content_type) && (
                                <CopyButton text={JSON.stringify(selectedLog?.response_record?.body)} />
                            )}
                        </span>
                    )}
                </Modal>
            )}

            <Table
                tableHeader={tableHeader}
                tbodyData={tableBody}
                data={data}
                identificator='response-details'
                isScrollable={data?.length > 10}
            />
        </>
    )
}

export default ResponseLogsTable
